import React, { useEffect, useMemo, useState } from 'react';
import { Form, Modal, Radio } from 'antd';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import type { TTBag } from '../graphql/mutations/createTTBag';
import { useWarehouseContext } from '../../../../contexts/WarehouseContext';
import {
    GET_CREATABLE_BAG_BUSINESS_TYPES,
    GetCreatableBagBusinessTypesResponse,
} from '../graphql/queries/getCreatableBagBusinessTypes';
import { BusinessType } from '../graphql/types/BagTagType';
import NewBagForm from './NewBagForm';
import NewTTBagForm from './NewTTBagForm';
import NewTemuBagForm from './NewTemuBagForm';

export type NewBagUnifiedModalType = 'swx' | 'tt' | 'temu';

// 后端包牌业务类型值 → 弹窗类型
const BUSINESS_TYPE_VALUE_TO_MODAL_TYPE: Record<number, NewBagUnifiedModalType> = {
    [BusinessType.SWIFTX]: 'swx',
    [BusinessType.TT_SORT]: 'tt',
    [BusinessType.TEMU_SORT]: 'temu',
};

// 默认仅 SwiftX（与后端一致）；分拣仓需显式配置 TT/TEMU
const DEFAULT_MODAL_TYPES: NewBagUnifiedModalType[] = ['swx'];

export interface NewBagUnifiedModalProps {
    visible: boolean;
    onClose: () => void;
    onSubmitSuccess: (bags: TTBag[], printCopies: number) => void;
    initialType?: NewBagUnifiedModalType;
}

const NewBagUnifiedModal: React.FC<NewBagUnifiedModalProps> = ({
    visible,
    onClose,
    onSubmitSuccess,
    initialType = 'swx',
}) => {
    const { t } = useTranslation();
    const { currentWarehouse } = useWarehouseContext();
    const [type, setType] = useState<NewBagUnifiedModalType>(initialType);

    const warehouseId = currentWarehouse?.id;

    const { data } = useQuery<GetCreatableBagBusinessTypesResponse>(GET_CREATABLE_BAG_BUSINESS_TYPES, {
        variables: { warehouseId },
        skip: !visible || !warehouseId,
        fetchPolicy: 'cache-and-network',
    });

    // 当前仓可创建的业务类型选项（后端未返回/为空时默认仅 SwiftX）
    const businessTypeOptions = useMemo(() => {
        const mapped = (data?.getCreatableBagBusinessTypes ?? [])
            .map((v) => BUSINESS_TYPE_VALUE_TO_MODAL_TYPE[v])
            .filter((v): v is NewBagUnifiedModalType => Boolean(v));
        const allowed = new Set<NewBagUnifiedModalType>(mapped.length > 0 ? mapped : DEFAULT_MODAL_TYPES);
        return [
            { label: t('wms.bags.unified.swiftx'), value: 'swx' as const },
            { label: t('wms.bags.unified.ttSort'), value: 'tt' as const },
            { label: t('wms.bags.unified.temuSort'), value: 'temu' as const },
        ].filter((o) => allowed.has(o.value));
    }, [t, data]);

    // 若当前选中类型不在可选项内（含仓库切换、初始类型被限制场景），自动切到首个可选项
    useEffect(() => {
        if (businessTypeOptions.length > 0 && !businessTypeOptions.some((o) => o.value === type)) {
            setType(businessTypeOptions[0].value);
        }
    }, [businessTypeOptions, type]);

    return (
        <Modal
            title={t('wms.bags.buttons.newBagTag')}
            open={visible}
            onCancel={onClose}
            footer={null}
            width={720}
            destroyOnClose
        >
            <Form layout="horizontal" labelCol={{ span: 7 }} wrapperCol={{ span: 17 }}>
                <Form.Item label={t('wms.bags.pageDetails.businessType')}>
                    <Radio.Group
                        options={businessTypeOptions}
                        value={type}
                        onChange={(e) => setType(e.target.value as NewBagUnifiedModalType)}
                        optionType="button"
                    />
                </Form.Item>
            </Form>

            <div style={{ display: type === 'swx' ? 'block' : 'none' }}>
                <NewBagForm visible={visible && type === 'swx'} onClose={onClose} onSubmitSuccess={onSubmitSuccess} />
            </div>
            <div style={{ display: type === 'tt' ? 'block' : 'none' }}>
                <NewTTBagForm visible={visible && type === 'tt'} onClose={onClose} onSubmitSuccess={onSubmitSuccess} />
            </div>
            <div style={{ display: type === 'temu' ? 'block' : 'none' }}>
                <NewTemuBagForm visible={visible && type === 'temu'} onClose={onClose} onSubmitSuccess={onSubmitSuccess} />
            </div>
        </Modal>
    );
};

export default NewBagUnifiedModal;
